import React, { PropsWithChildren, ReactNode } from 'react'
import styled, { css } from 'styled-components'
import { Flex, Text, mediaQueries } from 'pcln-design-system'
import { TierBadge, VipIcon } from '@pcln/vip'

import Banner from '../Banner'

/** @public */
export type VipBannerProps = {
  bgColor?: string
  borderHugLeft?: string
  borderHugTop?: string
  borderRadius?: string
  boxShadowSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
  buttonRight?: ReactNode
  buttonRightBreakPoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
  fontSizeCss?: string
  heading?: ReactNode | string
  hoverBoxShadowSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl'
  iconContainerProps?: Record<string, unknown>
  iconRight?: boolean
  iconSizeCss?: string
  imageLeft?: ReactNode
  includeIconText?: boolean
  isButtonFullWidthAtBreakPoint?: boolean
  isDark?: boolean
  justifyContent?: string
  mobileIconAlign?: 'flex-start' | 'center' | 'flex-end'
  px?: string | number | Array<string | number | null>
  py?: string | number | Array<string | number | null>
  text?: ReactNode | string
  vipTier?: string | boolean
}

const IconContainer = styled(Flex)`
  ${(props) => `align-self: ${props.mobileIconAlignSelf};`}
  ${mediaQueries.md} {
    align-self: center;
  }
`

const ResponsiveVipIcon = styled(VipIcon)`
  display: flex;
  ${(props) => props.fontSizeCss}
`

/** @public */
const VipBanner = ({
  bgColor = 'primary.light',
  borderHugLeft,
  borderHugTop,
  borderRadius,
  boxShadowSize,
  buttonRight,
  buttonRightBreakPoint = 'md',
  heading,
  hoverBoxShadowSize,
  iconContainerProps,
  iconRight,
  iconSizeCss,
  imageLeft,
  includeIconText,
  isButtonFullWidthAtBreakPoint,
  isDark,
  justifyContent,
  mobileIconAlign,
  px,
  py,
  text,
  vipTier,
}: PropsWithChildren<VipBannerProps>) => {
  let mobileIconAlignSelf = mobileIconAlign ?? 'flex-start'
  let fontSizeCss =
    iconSizeCss ??
    css`
      font-size: 28px;
      ${mediaQueries.md} {
        font-size: 48px;
      }
    `
  if (includeIconText) {
    mobileIconAlignSelf = 'center'
    fontSizeCss = css`
      font-size: 36px;
    `
  }

  const vipIconLeft =
    vipTier && includeIconText ? (
      <IconContainer mobileIconAlignSelf={mobileIconAlignSelf} {...iconContainerProps}>
        <TierBadge data-testid='left icon' layout='stacked' level={vipTier} pr={1} size='small' />
      </IconContainer>
    ) : (
      <IconContainer
        alignItems='center'
        data-testid='left icon'
        flexDirection='column'
        includeIconText={includeIconText}
        mobileIconAlignSelf={mobileIconAlignSelf}
        pr={includeIconText ? 1 : 0}
        {...iconContainerProps}
      >
        <ResponsiveVipIcon fontSizeCss={fontSizeCss} variation='vip' />
        {includeIconText && (
          <Text color={isDark ? 'text.lightest' : 'primary.dark'} fontWeight='bold' pt={2} fontSize={0}>
            MEMBER
          </Text>
        )}
      </IconContainer>
    )

  return (
    <Banner
      bgColor={bgColor}
      borderHugLeft={borderHugLeft}
      borderHugTop={borderHugTop}
      borderRadius={borderRadius}
      boxShadowSize={boxShadowSize}
      buttonRight={buttonRight}
      buttonRightBreakPoint={buttonRight ? buttonRightBreakPoint : undefined}
      heading={
        <Text fontSize={[2, null, 3]} pb={1} pr={iconRight && '25px'} color='primary.base'>
          {heading}
        </Text>
      }
      hoverBoxShadowSize={hoverBoxShadowSize}
      iconLeft={!iconRight && vipIconLeft}
      iconRight={iconRight && <VipIcon data-testid='right icon' fontSize='25px' variation='vip' />}
      imageLeft={imageLeft}
      isButtonFullWidthAtBreakPoint={isButtonFullWidthAtBreakPoint}
      justifyContent={justifyContent}
      px={px}
      py={py}
      text={
        <Text fontSize={[0, null, 1]} pr={iconRight && '25px'}>
          {text}
        </Text>
      }
    />
  )
}

export default VipBanner
